import classNames from "classnames"
import React from "react"

// The trade drawer's action buttons. It deliberately does not use the shared
// ModalButton: that one is built for full-screen modals — heavy gradients, a
// glassy overlay, a pulsing highlight and hover:scale-110 — which fights this
// drawer's flat single-colour surface and its thin-bordered, tinted panels, and
// three of them in one sidebar-wide row left the labels cramped.
//
// Instead: one solid button for the action the player is most likely to want and
// tinted outlines for the alternatives, so the row has a clear primary without
// every button competing. The buttons stretch to share the row's full width, so
// the tap targets are as large as the drawer allows.
type TradeButtonTone =
  // Solid amber, dark label. The loudest surface in the drawer and its accent
  // colour, so "send this" never has to be looked for.
  | "primary"
  // Solid emerald, reserved for accepting — the one irreversible yes.
  | "accept"
  // Tinted outline for the counter-offer: an alternative to yes/no, not a
  // third shout.
  | "counter"
  // Tinted red outline for decline / withdraw. Tinted rather than solid so a
  // mis-aimed thumb doesn't land on the loudest thing in the row.
  | "danger"
  // The quietest option, for abandoning edits that cost nothing.
  | "ghost"

type TradeActionButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: React.ReactNode
  tone?: TradeButtonTone
  // Font Awesome class, e.g. "fa-solid fa-check". Hidden from screen readers —
  // it only ever restates the label next to it.
  icon?: string
}

const toneClasses: Record<TradeButtonTone, string> = {
  primary:
    "border-amber-300 bg-amber-400 text-[#1b1205] hover:bg-amber-300 hover:border-amber-200",
  accept:
    "border-emerald-400 bg-emerald-500 text-white hover:bg-emerald-400 hover:border-emerald-300",
  counter:
    "border-white/20 bg-white/10 text-white hover:bg-white/20 hover:border-white/30",
  danger:
    "border-red-400/40 bg-red-500/20 text-red-200 hover:bg-red-500/30 hover:border-red-400/60",
  ghost:
    "border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white",
}

const TradeActionButton: React.FC<TradeActionButtonProps> = ({
  children,
  className,
  tone = "counter",
  icon,
  disabled,
  ...rest
}) => (
  <button
    {...rest}
    disabled={disabled}
    className={classNames(
      // flex-1 with a floor: equal shares of the row, but never squeezed to the
      // point where a label has to wrap.
      "flex flex-1 basis-0 min-w-[5rem] items-center justify-center gap-1.5",
      "h-10 px-2.5 rounded-lg border",
      "font-poppins font-semibold text-sm leading-none whitespace-nowrap",
      // Colour-only transitions plus a 1px press: nothing here changes the row's
      // layout, which matters because the drawer sizes itself to its content.
      "transition-colors duration-150 active:translate-y-px",
      toneClasses[tone],
      disabled && "opacity-50 cursor-not-allowed",
      className
    )}
  >
    {icon && <i aria-hidden className={classNames(icon, "text-xs")} />}
    {children}
  </button>
)

export default TradeActionButton
